// ===== Packages =====

import styled                               from 'styled-components';

// ===== Services =====

import {
    setColorLightness,
}                                           from '../../services';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    EDITOR_OUTLINE_SECTION_LIGHTNESS_VALUE,
}                                           from '../../constants/generalConstants';
import FONT_TYPE                            from '../../constants/fontType';

const OUTLINE_CHAPTER_MARGIN_BOTTOM = 0.625 * BODY_FONT_SIZE;
const DRAG_INDICATOR_LENGTH = 1.25 * BODY_FONT_SIZE;
const GENERAL_INDICATOR_LENGTH = 0.9375 * BODY_FONT_SIZE;
const CHAPTER_NON_EMPTY_INDICATOR_RIGHT = 0.3125 * BODY_FONT_SIZE;
const CHAPTER_ANNOTATIONS_INDICATOR_RIGHT = GENERAL_INDICATOR_LENGTH + CHAPTER_NON_EMPTY_INDICATOR_RIGHT + 0.3125 * BODY_FONT_SIZE;
const CHAPTER_DISABLED_INDICATOR_RIGHT = GENERAL_INDICATOR_LENGTH + CHAPTER_ANNOTATIONS_INDICATOR_RIGHT + 0.3125 * BODY_FONT_SIZE;

interface ContainerProps {
    isDragging: boolean,
}
export const Container = styled.div<ContainerProps>`
    background: inherit;
    margin-bottom: ${`${OUTLINE_CHAPTER_MARGIN_BOTTOM}px`};
    opacity: ${({ isDragging }) => (isDragging ? 0 : 1)};
`;

interface OutlineChapterHeadProps {
    selected?: boolean,
    disabled: boolean,
    multipleChapters: boolean,
    transitionDuration: number,
}
export const OutlineChapterHead = styled.div<OutlineChapterHeadProps>`
    position: relative;
    padding: ${`${0.625 * BODY_FONT_SIZE}px ${1.875 * BODY_FONT_SIZE}px`};
    padding-right: ${({ multipleChapters }) => (multipleChapters ? `${1.875 * BODY_FONT_SIZE}px` : `${0.625 * BODY_FONT_SIZE}px`)};
    border-radius: ${`${0.25 * BODY_FONT_SIZE}px`};
    border: ${({ selected, theme }) => (selected
        ? `${0.25 * BODY_FONT_SIZE}px solid ${setColorLightness(
            theme.verascopeColor.purple200,
            EDITOR_OUTLINE_SECTION_LIGHTNESS_VALUE,
        )}`
        : 'none'
    )};
    background: ${({ theme, disabled }) => (disabled ? theme.color.neutral100 : theme.color.white)};
    box-shadow: ${({ theme, disabled }) => (disabled ? 'none' : theme.color.boxShadow100)};
    transition: ${({ theme, transitionDuration }) => `
        box-shadow ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & svg {
        fill: ${({
        disabled,
        theme,
    }) => {
        if (disabled) {
            return `${theme.color.neutral800} !important`;
        }
    }}
    };

    color: ${({
        disabled,
        theme,
    }) => {
        if (disabled) {
            return `${theme.color.neutral800} !important`;
        }
    }};

    &:hover {
        box-shadow: ${({ theme }) => theme.color.boxShadow300};

        & + div {
            background: transparent;
        }
    }
`;

export const OutlineChapterTitleInputContainer = styled.div`
    padding-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    border-bottom: ${({ theme }) => `${0.125 * BODY_FONT_SIZE}px solid ${theme.color.neutral200}`};
`;

interface OutlineChapterTitleInputProps {
    customCursorActive: boolean,
}
export const OutlineChapterTitleInput = styled.input<OutlineChapterTitleInputProps>`
    position: relative;
    width: 100%;
    background: transparent;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1.2em;
    font-weight: 400;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral1000};
    text-transform: none;
    border: none;
    text-decoration: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'text')};

    &:focus{
        outline: none;
    }

    &::placeholder {
        color: ${({ theme }) => theme.color.neutral400};
    }
`;

interface OutlineChapterDescriptionInputProps {
    customCursorActive: boolean,
}
export const OutlineChapterDescriptionInput = styled.div<OutlineChapterDescriptionInputProps>`
    position: relative;
    background: transparent;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.9em;
    font-weight: 400;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral1000};
    padding: 0;
    white-space: pre-wrap;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'text')};

    &:empty:not(:focus):before {
        content: attr(data-placeholder);
        line-height: 1em;
        color: ${({ theme }) => theme.color.neutral400};
        pointer-events: none;
    }

    &:focus, &:hover {
        outline: none;
    }
`;

interface OutlineChapterDragIndicatorProps {
    horizontalOffset: number,
    transitionDuration: number,
}
export const OutlineChapterDragIndicator = styled.div<OutlineChapterDragIndicatorProps>`
    position: absolute;
    top: 50%;
    right: 0;
    transform: ${({ horizontalOffset }) => `translate(-${horizontalOffset}px, -50%)`};
    background: ${({ theme }) => theme.color.white};
    height: ${`${DRAG_INDICATOR_LENGTH}px`};
    width: ${`${DRAG_INDICATOR_LENGTH}px`};
    padding-right: ${`${0.125 * BODY_FONT_SIZE}px`};
    opacity: 0;
    transition: ${({ theme, transitionDuration }) => `
        opacity ${transitionDuration}ms ${theme.motion.eagerEasing},
        transform ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        height: 100%;
        fill: ${({ theme }) => theme.color.neutral500};
    }

    ${OutlineChapterHead}:hover & {
        opacity: 1;
        transform: translate(0px, -50%);
    };
`;

export const OutlineChapterBody = styled.div`
    position: relative;
    padding-left: ${`${1.875 * BODY_FONT_SIZE}px`};
    background: inherit;
`;

export const OutlineNumberText = styled.span`
    position: absolute;
    top: ${`${0.5625 * BODY_FONT_SIZE}px`};
    left: ${`${0.625 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.8em;
    font-weight: 400;
    color: ${({ theme }) => theme.verascopeColor.gray300};
`;

interface AddSectionButtonProps {
    disabled: boolean,
    bottomSectionSelected: boolean,
    chapterSelected: boolean,
    transitionDuration: number,
}
export const AddSectionButton = styled.div<AddSectionButtonProps>`
    position: absolute;
    left: ${({ chapterSelected }) => `${chapterSelected ? 0.5 * BODY_FONT_SIZE : 0.25 * BODY_FONT_SIZE}px`};
    bottom: ${({ bottomSectionSelected }) => `${bottomSectionSelected ? 0.5625 * BODY_FONT_SIZE : 0.3125 * BODY_FONT_SIZE}px`};
    width: ${`${1.375 * BODY_FONT_SIZE}px`};
    height: ${`${1.375 * BODY_FONT_SIZE}px`};
    margin-top: ${`${0.3125 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.6875 * BODY_FONT_SIZE}px`};
    background: ${({ theme, disabled }) => (disabled ? theme.color.neutral100 : theme.color.neutral400)};
    box-shadow: ${({ theme, disabled }) => (disabled ? 'none' : theme.color.boxShadow100)};
    transition: ${({ theme, transitionDuration }) => `
        box-shadow ${transitionDuration}ms ${theme.motion.standardEasing}
    `};
`;

export const AddSectionButtonIcon = styled.div`
    display: inline-block;
    position: relative;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: ${`${1 * BODY_FONT_SIZE}px`};
    height: ${`${1 * BODY_FONT_SIZE}px`};
    transition: ${({ theme }) => `transform 300ms ${theme.motion.standardEasing}`};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        fill: ${({ theme }) => theme.color.white};
        width: 100%;
        height: 100%;
    }

    ${AddSectionButton}:hover & {
        transform: translate(-50%, -50%) rotate(180deg);
    }
`;

interface ChapterNonEmptyIndicatorProps {
    isVisible: boolean,
    transitionDuration: number,
}
export const ChapterNonEmptyIndicator = styled.div<ChapterNonEmptyIndicatorProps>`
    position: absolute;
    bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    right: ${`${CHAPTER_NON_EMPTY_INDICATOR_RIGHT}px`};
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    z-index: 2; // Place above non-empty indicator in z-space
    transition: ${({ theme, transitionDuration }) => `
        opacity ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & div > svg {
        display: flex;
        align-items: center;
        justify-content: center;
        height: ${`${GENERAL_INDICATOR_LENGTH}px`};
        width: ${`${GENERAL_INDICATOR_LENGTH}px`};
    }

    & svg {
        height: 100%;
        fill: ${({ theme }) => theme.color.neutral500};
    }
`;

interface ChapterAnnotationsIndicatorProps {
    isVisible: boolean,
    nonEmptyIndicatorVisible: boolean,
    transitionDuration: number,
}
export const ChapterAnnotationsIndicator = styled.div<ChapterAnnotationsIndicatorProps>`
    position: absolute;
    bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    right: ${({ nonEmptyIndicatorVisible }) => `${nonEmptyIndicatorVisible
        ? CHAPTER_ANNOTATIONS_INDICATOR_RIGHT
        : CHAPTER_NON_EMPTY_INDICATOR_RIGHT
    }px`};
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    z-index: 1;
    transition: ${({ theme, transitionDuration }) => `
        opacity ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & div > svg {
        display: flex;
        align-items: center;
        justify-content: center;
        height: ${`${GENERAL_INDICATOR_LENGTH}px`};
        width: ${`${GENERAL_INDICATOR_LENGTH}px`};
    }

    & svg {
        height: 100%;
        fill: ${({ theme }) => theme.color.neutral500};
    }
`;

interface ChapterDisabledIndicatorProps {
    annotationsIndicatorVisible: boolean,
}
export const ChapterDisabledIndicator = styled(ChapterAnnotationsIndicator)<ChapterDisabledIndicatorProps>`
    right: ${({ nonEmptyIndicatorVisible, annotationsIndicatorVisible }) => {
        if (nonEmptyIndicatorVisible && annotationsIndicatorVisible) {
            return `${CHAPTER_DISABLED_INDICATOR_RIGHT}px`;
        }
        if (nonEmptyIndicatorVisible && !annotationsIndicatorVisible) {
            return `${CHAPTER_ANNOTATIONS_INDICATOR_RIGHT}px`;
        }
        return CHAPTER_NON_EMPTY_INDICATOR_RIGHT;
    }};
`;
